import { EDebuggableModuleState, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { EIndependenceType } from '@fragmentsQueue/IFragmentsQueue';
import { BasePlaylistRotationController } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController';
import { IBasePlaylistRotationControllerConfig } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.config';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { PlaylistDecryptHandler } from '@streamLoader/PlaylistDecrypt/PlaylistDecryptHandler';
import { NetworkLoader } from '@streamLoader/net/NetworkLoader';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';
import { getGlobalThis } from '@utils/polyfills/globalThis';

export class SimplePlaylistRotationController extends BasePlaylistRotationController {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['loader'];
  public additionalInfo: TDebuggableAdditionalInfo = {};

  private _playlistLoaderTimeout: ReturnType<typeof setTimeout> | null = null;
  private _lastTargetDuration: number = 2;

  constructor(eventBus: TEventBus, url: string, playlistDecryptHandler: PlaylistDecryptHandler, startManifest?: IHydratedManifest) {
    super('SimplePlaylistRotationController', 'playlistLoader', playlistDecryptHandler, eventBus);

    this._lifecycleEventReporter('Created');
    this._url = url;

    if (startManifest) {
      this._handleParsedPlaylist(startManifest);
    } else {
      this._loadNextPlaylist();
    }
  }

  public destroy(): void {
    this._isActive = false;
    super.destroy();

    this._currentPlaylistLoader?.destroy();
    this._currentPlaylistLoader = null;
    this._logger.info('Destroy controller');

    if (this._playlistLoaderTimeout) {
      clearTimeout(this._playlistLoaderTimeout);
    }

    this._lifecycleEventReporter('Destroyed');
  }

  public abortCurrentPlaylistLoading(): void {
    this._logger.info('Aborting current loading playlist');
    this._currentPlaylistLoader?.destroy();
  }

  public setURL(url: string, soft?: boolean): void {
    if (soft) {
      this._url = url;
      return;
    }

    this._logger.info(`Setting new url ${url}`);
    this._currentPlaylistLoader?.destroy();
    this._url = url;
    this._loadNextPlaylist();
  }

  @bind
  public _handleConfigUpdated(updatedValues: Partial<IBasePlaylistRotationControllerConfig>): void {
    if (updatedValues.equalPlaylistsAmountToDecline) {
      this._samePlaylistsCount = 0;
    }
  }

  protected _loadNextPlaylist(): void {
    if (!this._isActive) {
      this._logger.warn('Loader was destroying, but we trying to load next playlist!');
      return;
    }

    const { playlistLoadingTimeout } = this.config;

    this._logger.debug('Loading next playlist');
    this._lifecycleEventReporter(`Loading next playlist with url ${this._url}`);
    this.status = 'Loading playlist';

    if (this._currentPlaylistLoader) {
      this._currentPlaylistLoader.cancel(`Another playlist with url ${this._url} scheduled to load`);
    }

    if (!this._url) {
      return;
    }

    this._currentPlaylistLoader = new NetworkLoader(
      {
        url: this._buildUrl(),
        loadingTimeout: playlistLoadingTimeout,
        autoLoad: true,
        mode: 'text',
        onStateChanged: this._onPlaylistLoadingStateChanged,
        workerMode: this.config.workerMode,
        requestCustomReferer: this.config.sendCustomRefererHeader ? getGlobalThis().location?.href : undefined,
      },
      this._eventBus,
    );

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.playlistRequested, {});
  }

  protected _handleParsedPlaylist(manifest: IHydratedManifest): void {
    this._lifecycleEventReporter('Playlist parsed');

    if (this._isMultiVariantPlaylist(manifest)) {
      this._handleMultivariantManifestDetected(manifest);

      return;
    }

    if (this._isLowLatencyPlaylist(manifest) && this.config.lowLatencyMode) {
      this._handleLLManifestDetected(manifest);

      return;
    }

    if (this._isVODPlaylist(manifest)) {
      this._handleVODPlaylist(manifest);

      return;
    }

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.lastPlaylistOfSegmentLoaded, {});

    if (!this._url) {
      return;
    }

    const url: string = this._url;

    this._lastTargetDuration = manifest.targetDuration || 2;

    const fragments: Fragment[] = manifest.segments.map(
      (segment) =>
        new Fragment(
          {
            fragmentUrl: segment.uri,
            playlistUrl: url,
            duration: segment.duration,
            independent: EIndependenceType.INDEPENDENT,
            msn: segment.msnNumber,
            pn: segment.partNumber,
            isTransmuxingBypassRequested: !!manifest.segments[0].map?.uri,
          },
          this._eventBus,
          this.config.shouldLogFragmentsHistory,
        ),
    );

    if (manifest.segments[0]?.map?.uri) {
      fragments.unshift(
        new Fragment(
          {
            fragmentUrl: manifest.segments[0].map.uri,
            playlistUrl: url,
            independent: EIndependenceType.INDEPENDENT,
            msn: -1,
            pn: -1,
            isTransmuxingBypassRequested: true,
            duration: 0,
            isInitSection: true,
          },
          this._eventBus,
          this.config.shouldLogFragmentsHistory,
        ),
      );
    }

    this._handleFragmentsDetected(fragments);

    this._planNextLoad();
  }

  protected _planNextLoad(): void {
    const timeout: number = this._getNextPlaylistLoadTimeout();

    if (this._currentPlaylistLoader) {
      this._currentPlaylistLoader.destroy();
      this._currentPlaylistLoader = null;
    }

    this._logger.info(`Expecting next load in ${timeout} ms`);
    this._lifecycleEventReporter('Awaiting next loading');
    this.status = 'Awaiting next loading';
    this._playlistLoaderTimeout = globalThis.setTimeout(() => {
      this._loadNextPlaylist();
    }, timeout);
  }

  private _getNextPlaylistLoadTimeout(): number {
    const { forceStaticPlaylistLoadingTimeout, staticPlaylistLoadingTimeoutInDurations } = this.config;
    if (forceStaticPlaylistLoadingTimeout && staticPlaylistLoadingTimeoutInDurations) {
      this._logger.verbose(
        `forceStaticPlaylistLoadingTimeout is set to true and staticPlaylistLoadingTimeoutInDurations is set to ${staticPlaylistLoadingTimeoutInDurations}. Using static timeout`,
      );
      return staticPlaylistLoadingTimeoutInDurations * this._lastTargetDuration * 1000;
    }

    const multiplier: number = !this.config.disableRotateTimeoutIncrease && this._samePlaylistsCount > 1 ? 1.5 : 1;
    this._logger.verbose(
      `There was ${this._samePlaylistsCount} same playlists. Multiplier is ${multiplier}. Target duration is ${this._lastTargetDuration}`,
    );

    return multiplier * this._lastTargetDuration * 1000;
  }

  private _buildUrl(): URL {
    const url: URL = new URL(this._url || '');

    url.search = new URLSearchParams([...this._playlistDecryptHandler.corruptionQueryParams, ...url.searchParams]).toString();
    return url;
  }
}
